import { createSelector, createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { salesPlaybookApi } from '../../services/apiService';
import { PLAYBOOK_UNNAMED_LABEL } from '../../constants/salesPlaybook';
import { migrateLegacyBulletTracking, PlaybookBullet, resolvePlaybookBullets } from '../../utils/playbookItemIds';

export type SalesPlaybookTag = 'situation' | 'pain' | 'cost_math' | 'authority' | 'timeline' | 'close_trigger';

export interface SalesPlaybookQuestion {
  _id: string;
  questionText: string;
  tags: SalesPlaybookTag[];
  sortOrder: number;
  isActive: boolean;
}

export interface SalesPlaybookRoleInfo {
  name: string;
  displayName: string;
}

export interface SalesPlaybookInfo {
  playbookName: string;
  displayName: string;
  roles: SalesPlaybookRoleInfo[];
}

export interface RoleData {
  playbookName: string;
  playbookDisplayName: string;
  roleId: string;
  roleName: string;
  roleDisplayName: string;
  pitch: string[];
  advanced: string[];
  pitchItems?: Array<{ id: string; text: string }>;
  advancedItems?: Array<{ id: string; text: string }>;
  questions: SalesPlaybookQuestion[];
}

interface ItemNote {
  note: string;
  timestamp: string;
  source?: 'ai' | 'human';
}

export interface LivePlaybookItem {
  section: 'pitch' | 'question' | 'advanced';
  id: string;
  checked: boolean;
  note: string;
  source?: 'ai' | 'human' | null;
  locked?: boolean;
  justChecked?: boolean;
  /** A note the service just added from the customer's response (the second event). */
  justNoted?: boolean;
}

// Tracking data stored per playbook+role combination
export interface SelectionTrackingData {
  askedQuestions: string[];
  questionNotes: Record<string, ItemNote>;
  checkedPitches: string[];
  pitchNotes: Record<string, ItemNote>;
  checkedAdvanced: string[];
  advancedNotes: Record<string, ItemNote>;
  legacyPitchTexts?: Record<string, string>;
  legacyAdvancedTexts?: Record<string, string>;
}

export interface SalesPlaybookState {
  // Available playbooks (for dropdown)
  playbooks: SalesPlaybookInfo[];
  playbooksLoading: boolean;

  // Current selection
  selectedPlaybookName: string | null;
  selectedPlaybookDisplayName: string | null;
  selectedRole: string;
  selectedRoleDisplayName: string | null;

  // Current role's data (pitch, advanced, and questions for selected role)
  currentPitch: string[];
  currentAdvanced: string[];
  currentPitchItems: PlaybookBullet[];
  currentAdvancedItems: PlaybookBullet[];
  availableRoles: SalesPlaybookRoleInfo[];
  questions: SalesPlaybookQuestion[];
  dataLoading: boolean;

  // Live call tracking (for current selection)
  askedQuestions: string[]; // Question IDs marked as asked
  questionNotes: Record<string, ItemNote>;
  checkedPitches: string[]; // Stable pitch IDs; legacy index IDs are migrated on role fetch
  pitchNotes: Record<string, ItemNote>;
  checkedAdvanced: string[]; // Stable advanced IDs; legacy index IDs are migrated on role fetch
  advancedNotes: Record<string, ItemNote>;
  legacyPitchTexts: Record<string, string>;
  legacyAdvancedTexts: Record<string, string>;
  manualOverrides: Record<string, boolean>;
  pendingManualOverrides: Record<string, boolean>;
  liveCheckAnimations: Record<string, boolean>;
  liveNoteAnimations: Record<string, boolean>;

  // Cache of tracking data per playbook+role (key: "playbookName|role")
  trackingCache: Record<string, SelectionTrackingData>;

  error: string | null;
}

export interface SalesPlaybookRestorePayload {
  selectedPlaybookName: string;
  selectedPlaybookDisplayName: string;
  selectedRole: string;
  selectedRoleDisplayName: string;
  availableRoles: SalesPlaybookRoleInfo[];
  askedQuestions: string[];
  questionNotes: Record<string, ItemNote>;
  checkedPitches?: string[];
  pitchNotes?: Record<string, ItemNote>;
  checkedAdvanced?: string[];
  advancedNotes?: Record<string, ItemNote>;
  legacyPitchTexts?: Record<string, string>;
  legacyAdvancedTexts?: Record<string, string>;
  trackingCache?: Record<string, SelectionTrackingData>;
}

// Helper to create cache key
const getTrackingCacheKey = (playbookName: string, role: string): string => `${playbookName}|${role}`;

const initialState: SalesPlaybookState = {
  playbooks: [],
  playbooksLoading: false,
  selectedPlaybookName: null,
  selectedPlaybookDisplayName: null,
  selectedRole: 'general',
  selectedRoleDisplayName: 'General',
  currentPitch: [],
  currentAdvanced: [],
  currentPitchItems: [],
  currentAdvancedItems: [],
  availableRoles: [],
  questions: [],
  dataLoading: false,
  askedQuestions: [],
  questionNotes: {},
  checkedPitches: [],
  pitchNotes: {},
  checkedAdvanced: [],
  advancedNotes: {},
  legacyPitchTexts: {},
  legacyAdvancedTexts: {},
  manualOverrides: {},
  pendingManualOverrides: {},
  liveCheckAnimations: {},
  liveNoteAnimations: {},
  trackingCache: {},
  error: null
};

// Async thunks
export const fetchPlaybooks = createAsyncThunk(
  'salesPlaybook/fetchPlaybooks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await salesPlaybookApi.getPlaybooks();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch playbooks');
    }
  }
);

export const fetchRoleData = createAsyncThunk<
  RoleData | null,
  { playbookName: string; roleName: string },
  { rejectValue: string }
>(
  'salesPlaybook/fetchRoleData',
  async ({ playbookName, roleName }, { rejectWithValue }) => {
    try {
      const response = await salesPlaybookApi.getRoleData(playbookName, roleName);
      return response as RoleData;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch role data');
    }
  }
);

const salesPlaybookSlice = createSlice({
  name: 'salesPlaybook',
  initialState,
  reducers: {
    setSelectedPlaybook: (state, action: PayloadAction<{ playbookName: string; displayName: string; roles: SalesPlaybookRoleInfo[] } | null>) => {
      // Always save current role's tracking data to cache before switching
      if (state.selectedPlaybookName && state.selectedRole) {
        const currentKey = getTrackingCacheKey(state.selectedPlaybookName, state.selectedRole);
        // Deep copy all notes to avoid reference issues
        const questionNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.questionNotes).forEach(key => {
          questionNotesCopy[key] = { ...state.questionNotes[key] };
        });
        const pitchNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.pitchNotes).forEach(key => {
          pitchNotesCopy[key] = { ...state.pitchNotes[key] };
        });
        const advancedNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.advancedNotes).forEach(key => {
          advancedNotesCopy[key] = { ...state.advancedNotes[key] };
        });
        state.trackingCache[currentKey] = {
          askedQuestions: [...state.askedQuestions],
          questionNotes: questionNotesCopy,
          checkedPitches: [...state.checkedPitches],
          pitchNotes: pitchNotesCopy,
          checkedAdvanced: [...state.checkedAdvanced],
          advancedNotes: advancedNotesCopy,
          legacyPitchTexts: { ...state.legacyPitchTexts },
          legacyAdvancedTexts: { ...state.legacyAdvancedTexts }
        };
        console.log('[SalesPlaybook] setSelectedPlaybook - Saved to cache:', currentKey);
      }

      if (action.payload) {
        state.selectedPlaybookName = action.payload.playbookName;
        state.selectedPlaybookDisplayName = action.payload.displayName;
        state.availableRoles = action.payload.roles;
        state.currentPitch = [];
        state.currentAdvanced = [];
        state.currentPitchItems = [];
        state.currentAdvancedItems = [];
        state.questions = [];

        // Use 'general' role if it exists, otherwise use the first available role
        const generalRole = action.payload.roles.find(r => r.name === 'general');
        const defaultRole = generalRole || action.payload.roles[0];
        state.selectedRole = defaultRole?.name || 'general';
        state.selectedRoleDisplayName = defaultRole?.displayName || 'General';

        // Check if we have cached data for this playbook/role combination
        const newKey = getTrackingCacheKey(action.payload.playbookName, state.selectedRole);
        const cached = state.trackingCache[newKey];
        if (cached) {
          // Restore from cache
          state.askedQuestions = [...(cached.askedQuestions || [])];
          state.checkedPitches = [...(cached.checkedPitches || [])];
          state.checkedAdvanced = [...(cached.checkedAdvanced || [])];
          state.legacyPitchTexts = { ...(cached.legacyPitchTexts || {}) };
          state.legacyAdvancedTexts = { ...(cached.legacyAdvancedTexts || {}) };
          // Deep copy when restoring
          const restoredQuestionNotes: Record<string, ItemNote> = {};
          Object.keys(cached.questionNotes || {}).forEach(key => {
            restoredQuestionNotes[key] = { ...cached.questionNotes[key] };
          });
          state.questionNotes = restoredQuestionNotes;
          const restoredPitchNotes: Record<string, ItemNote> = {};
          Object.keys(cached.pitchNotes || {}).forEach(key => {
            restoredPitchNotes[key] = { ...cached.pitchNotes[key] };
          });
          state.pitchNotes = restoredPitchNotes;
          const restoredAdvancedNotes: Record<string, ItemNote> = {};
          Object.keys(cached.advancedNotes || {}).forEach(key => {
            restoredAdvancedNotes[key] = { ...cached.advancedNotes[key] };
          });
          state.advancedNotes = restoredAdvancedNotes;
          console.log('[SalesPlaybook] setSelectedPlaybook - Restored from cache:', newKey);
        } else {
          // No cache for this combination, start fresh
          state.askedQuestions = [];
          state.questionNotes = {};
          state.checkedPitches = [];
          state.pitchNotes = {};
          state.checkedAdvanced = [];
          state.advancedNotes = {};
          state.legacyPitchTexts = {};
          state.legacyAdvancedTexts = {};
          console.log('[SalesPlaybook] setSelectedPlaybook - No cache found, starting fresh:', newKey);
        }
      } else {
        state.selectedPlaybookName = null;
        state.selectedPlaybookDisplayName = null;
        state.availableRoles = [];
        state.selectedRole = 'general';
        state.selectedRoleDisplayName = null;
        state.currentPitch = [];
        state.currentAdvanced = [];
        state.currentPitchItems = [];
        state.currentAdvancedItems = [];
        state.questions = [];
        state.askedQuestions = [];
        state.questionNotes = {};
        state.checkedPitches = [];
        state.pitchNotes = {};
        state.checkedAdvanced = [];
        state.advancedNotes = {};
        state.legacyPitchTexts = {};
        state.legacyAdvancedTexts = {};
        state.trackingCache = {};
        state.manualOverrides = {};
        state.pendingManualOverrides = {};
      }
    },

    setSelectedRole: (state, action: PayloadAction<{ name: string; displayName: string }>) => {
      // Save current tracking data to cache before switching role
      if (state.selectedPlaybookName && state.selectedRole) {
        const currentKey = getTrackingCacheKey(state.selectedPlaybookName, state.selectedRole);
        // Deep copy all notes to avoid reference issues
        const questionNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.questionNotes).forEach(key => {
          questionNotesCopy[key] = { ...state.questionNotes[key] };
        });
        const pitchNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.pitchNotes).forEach(key => {
          pitchNotesCopy[key] = { ...state.pitchNotes[key] };
        });
        const advancedNotesCopy: Record<string, ItemNote> = {};
        Object.keys(state.advancedNotes).forEach(key => {
          advancedNotesCopy[key] = { ...state.advancedNotes[key] };
        });
        state.trackingCache[currentKey] = {
          askedQuestions: [...state.askedQuestions],
          questionNotes: questionNotesCopy,
          checkedPitches: [...state.checkedPitches],
          pitchNotes: pitchNotesCopy,
          checkedAdvanced: [...state.checkedAdvanced],
          advancedNotes: advancedNotesCopy,
          legacyPitchTexts: { ...state.legacyPitchTexts },
          legacyAdvancedTexts: { ...state.legacyAdvancedTexts }
        };
        console.log('[SalesPlaybook] Saved to cache:', currentKey);
      }

      const previousRole = state.selectedRole;
      state.selectedRole = action.payload.name;
      state.selectedRoleDisplayName = action.payload.displayName;
      state.currentPitch = [];
      state.currentAdvanced = [];
      state.currentPitchItems = [];
      state.currentAdvancedItems = [];
      state.questions = [];

      // Restore tracking from cache if available
      if (state.selectedPlaybookName) {
        const newKey = getTrackingCacheKey(state.selectedPlaybookName, action.payload.name);
        const cached = state.trackingCache[newKey];
        console.log('[SalesPlaybook] Switching from', previousRole, 'to', action.payload.name);
        if (cached) {
          // Deep copy when restoring
          state.askedQuestions = [...(cached.askedQuestions || [])];
          state.checkedPitches = [...(cached.checkedPitches || [])];
          state.checkedAdvanced = [...(cached.checkedAdvanced || [])];
          state.legacyPitchTexts = { ...(cached.legacyPitchTexts || {}) };
          state.legacyAdvancedTexts = { ...(cached.legacyAdvancedTexts || {}) };
          const restoredQuestionNotes: Record<string, ItemNote> = {};
          Object.keys(cached.questionNotes || {}).forEach(key => {
            restoredQuestionNotes[key] = { ...cached.questionNotes[key] };
          });
          state.questionNotes = restoredQuestionNotes;
          const restoredPitchNotes: Record<string, ItemNote> = {};
          Object.keys(cached.pitchNotes || {}).forEach(key => {
            restoredPitchNotes[key] = { ...cached.pitchNotes[key] };
          });
          state.pitchNotes = restoredPitchNotes;
          const restoredAdvancedNotes: Record<string, ItemNote> = {};
          Object.keys(cached.advancedNotes || {}).forEach(key => {
            restoredAdvancedNotes[key] = { ...cached.advancedNotes[key] };
          });
          state.advancedNotes = restoredAdvancedNotes;
          console.log('[SalesPlaybook] Restored from cache');
        } else {
          state.askedQuestions = [];
          state.questionNotes = {};
          state.checkedPitches = [];
          state.pitchNotes = {};
          state.checkedAdvanced = [];
          state.advancedNotes = {};
          state.legacyPitchTexts = {};
          state.legacyAdvancedTexts = {};
          console.log('[SalesPlaybook] No cache found, starting fresh');
        }
      }
    },

    markQuestionAsked: (state, action: PayloadAction<string>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload}`] = true;
      if (!state.askedQuestions.includes(action.payload)) {
        state.askedQuestions.push(action.payload);
      }
    },

    unmarkQuestionAsked: (state, action: PayloadAction<string>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload}`] = true;
      state.askedQuestions = state.askedQuestions.filter(id => id !== action.payload);
    },

    toggleQuestionAsked: (state, action: PayloadAction<string>) => {
      const questionId = action.payload;
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${questionId}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${questionId}`] = true;
      if (state.askedQuestions.includes(questionId)) {
        state.askedQuestions = state.askedQuestions.filter(id => id !== questionId);
        console.log('[SalesPlaybook] Unmarked question:', questionId, 'Current role:', state.selectedRole);
      } else {
        state.askedQuestions.push(questionId);
        console.log('[SalesPlaybook] Marked question:', questionId, 'Current role:', state.selectedRole);
      }
      console.log('[SalesPlaybook] askedQuestions now:', state.askedQuestions);
    },

    addQuestionNote: (state, action: PayloadAction<{ questionId: string; note: string }>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload.questionId}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload.questionId}`] = true;
      state.questionNotes[action.payload.questionId] = {
        note: action.payload.note,
        timestamp: new Date().toISOString(), source: 'human'
      };
      console.log('[SalesPlaybook] Added note for question:', action.payload.questionId, 'Current role:', state.selectedRole);
    },

    removeQuestionNote: (state, action: PayloadAction<string>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|question|${action.payload}`] = true;
      delete state.questionNotes[action.payload];
    },

    // Pitch tracking
    togglePitchChecked: (state, action: PayloadAction<string>) => {
      const pitchId = action.payload;
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|pitch|${pitchId}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|pitch|${pitchId}`] = true;
      if (state.checkedPitches.includes(pitchId)) {
        state.checkedPitches = state.checkedPitches.filter(id => id !== pitchId);
        console.log('[SalesPlaybook] Unchecked pitch:', pitchId);
      } else {
        state.checkedPitches.push(pitchId);
        console.log('[SalesPlaybook] Checked pitch:', pitchId);
      }
    },

    addPitchNote: (state, action: PayloadAction<{ pitchId: string; note: string }>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|pitch|${action.payload.pitchId}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|pitch|${action.payload.pitchId}`] = true;
      state.pitchNotes[action.payload.pitchId] = {
        note: action.payload.note,
        timestamp: new Date().toISOString(), source: 'human'
      };
      console.log('[SalesPlaybook] Added note for pitch:', action.payload.pitchId);
    },

    removePitchNote: (state, action: PayloadAction<string>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|pitch|${action.payload}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|pitch|${action.payload}`] = true;
      delete state.pitchNotes[action.payload];
    },

    // Advanced tracking
    toggleAdvancedChecked: (state, action: PayloadAction<string>) => {
      const advancedId = action.payload;
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|advanced|${advancedId}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|advanced|${advancedId}`] = true;
      if (state.checkedAdvanced.includes(advancedId)) {
        state.checkedAdvanced = state.checkedAdvanced.filter(id => id !== advancedId);
        console.log('[SalesPlaybook] Unchecked advanced:', advancedId);
      } else {
        state.checkedAdvanced.push(advancedId);
        console.log('[SalesPlaybook] Checked advanced:', advancedId);
      }
    },

    addAdvancedNote: (state, action: PayloadAction<{ advancedId: string; note: string }>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|advanced|${action.payload.advancedId}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|advanced|${action.payload.advancedId}`] = true;
      state.advancedNotes[action.payload.advancedId] = {
        note: action.payload.note,
        timestamp: new Date().toISOString(), source: 'human'
      };
      console.log('[SalesPlaybook] Added note for advanced:', action.payload.advancedId);
    },

    removeAdvancedNote: (state, action: PayloadAction<string>) => {
      state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|advanced|${action.payload}`] = true;
      state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|advanced|${action.payload}`] = true;
      delete state.advancedNotes[action.payload];
    },

    applyLivePlaybookItems: (state, action: PayloadAction<{ playbookName: string; role: string; items: LivePlaybookItem[]; snapshot?: boolean }>) => {
      const { playbookName, role, items, snapshot } = action.payload;
      if (state.selectedPlaybookName !== playbookName || state.selectedRole !== role) return;
      for (const item of items) {
        const lockKey = `${playbookName}|${role}|${item.section}|${item.id}`;
        const checked = item.section === 'question' ? state.askedQuestions
          : item.section === 'pitch' ? state.checkedPitches : state.checkedAdvanced;
        const notes = item.section === 'question' ? state.questionNotes
          : item.section === 'pitch' ? state.pitchNotes : state.advancedNotes;
        if (state.pendingManualOverrides[lockKey]) {
          // Wait for a snapshot that confirms the rep's edit before accepting
          // any later reset from another device. A stale snapshot is not an ack.
          if (snapshot && item.locked && item.checked === checked.includes(item.id) &&
              item.note === (notes[item.id]?.note || '')) {
            delete state.pendingManualOverrides[lockKey];
          }
          continue;
        }
        const serverReset = snapshot && !item.locked && (item.source === 'ai' ||
          (!item.checked && !item.note && !item.source));
        if (state.manualOverrides[lockKey]) {
          if (!serverReset) continue;
          delete state.manualOverrides[lockKey];
          delete notes[item.id];
        }
        if (item.checked && !checked.includes(item.id)) checked.push(item.id);
        if (snapshot && !item.checked) {
          const index = checked.indexOf(item.id);
          if (index >= 0) checked.splice(index, 1);
        }
        if (item.note && (!notes[item.id] || notes[item.id].source === 'ai')) {
          notes[item.id] = { note: item.note, timestamp: new Date().toISOString(), source: item.source || (snapshot ? 'human' : 'ai') };
        }
        if (snapshot && !item.note && notes[item.id]?.source === 'ai') delete notes[item.id];
        if (item.locked || item.source === 'human' || (snapshot && !item.source && (item.checked || item.note))) {
          state.manualOverrides[lockKey] = true;
        }
        if (item.justChecked) state.liveCheckAnimations[`${item.section}|${item.id}`] = true;
        if (item.justNoted && notes[item.id]?.source === 'ai') state.liveNoteAnimations[`${item.section}|${item.id}`] = true;
      }
    },

    clearLiveCheckAnimation: (state, action: PayloadAction<string>) => {
      delete state.liveCheckAnimations[action.payload];
    },

    clearLiveNoteAnimation: (state, action: PayloadAction<string>) => {
      delete state.liveNoteAnimations[action.payload];
    },

    resetLivePlaybookItem: (state, action: PayloadAction<{ section: LivePlaybookItem['section']; id: string }>) => {
      const { section, id } = action.payload;
      delete state.manualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|${section}|${id}`];
      delete state.pendingManualOverrides[`${state.selectedPlaybookName}|${state.selectedRole}|${section}|${id}`];
      if (section === 'question') {
        state.askedQuestions = state.askedQuestions.filter(value => value !== id);
        delete state.questionNotes[id];
      } else if (section === 'pitch') {
        state.checkedPitches = state.checkedPitches.filter(value => value !== id);
        delete state.pitchNotes[id];
      } else {
        state.checkedAdvanced = state.checkedAdvanced.filter(value => value !== id);
        delete state.advancedNotes[id];
      }
    },

    clearSalesPlaybookSession: (state) => {
      state.selectedPlaybookName = null;
      state.selectedPlaybookDisplayName = null;
      state.selectedRole = 'general';
      state.selectedRoleDisplayName = null;
      state.availableRoles = [];
      state.currentPitch = [];
      state.currentAdvanced = [];
      state.currentPitchItems = [];
      state.currentAdvancedItems = [];
      state.questions = [];
      state.askedQuestions = [];
      state.questionNotes = {};
      state.checkedPitches = [];
      state.pitchNotes = {};
      state.checkedAdvanced = [];
      state.advancedNotes = {};
      state.legacyPitchTexts = {};
      state.legacyAdvancedTexts = {};
      state.trackingCache = {}; // Clear the per-selection cache
      state.manualOverrides = {};
      state.pendingManualOverrides = {};
      state.error = null;
    },

    clearError: (state) => {
      state.error = null;
    },

    restoreSalesPlaybookState: (state, action: PayloadAction<SalesPlaybookRestorePayload>) => {
      state.selectedPlaybookName = action.payload.selectedPlaybookName;
      state.selectedPlaybookDisplayName = action.payload.selectedPlaybookDisplayName;
      state.selectedRole = action.payload.selectedRole;
      state.selectedRoleDisplayName = action.payload.selectedRoleDisplayName;
      state.availableRoles = action.payload.availableRoles;
      state.currentPitch = [];
      state.currentAdvanced = [];
      state.currentPitchItems = [];
      state.currentAdvancedItems = [];
      state.questions = [];
      state.askedQuestions = action.payload.askedQuestions;
      state.questionNotes = action.payload.questionNotes;
      state.checkedPitches = action.payload.checkedPitches || [];
      state.pitchNotes = action.payload.pitchNotes || {};
      state.checkedAdvanced = action.payload.checkedAdvanced || [];
      state.advancedNotes = action.payload.advancedNotes || {};
      state.legacyPitchTexts = action.payload.legacyPitchTexts || {};
      state.legacyAdvancedTexts = action.payload.legacyAdvancedTexts || {};
      state.manualOverrides = {};
      state.pendingManualOverrides = {};

      // Restore tracking cache if provided, otherwise populate with current selection's data
      if (action.payload.trackingCache) {
        state.trackingCache = action.payload.trackingCache;
      } else {
        // At minimum, cache the current selection's data
        const key = getTrackingCacheKey(action.payload.selectedPlaybookName, action.payload.selectedRole);
        state.trackingCache[key] = {
          askedQuestions: action.payload.askedQuestions,
          questionNotes: action.payload.questionNotes,
          checkedPitches: action.payload.checkedPitches || [],
          pitchNotes: action.payload.pitchNotes || {},
          checkedAdvanced: action.payload.checkedAdvanced || [],
          advancedNotes: action.payload.advancedNotes || {},
          legacyPitchTexts: action.payload.legacyPitchTexts || {},
          legacyAdvancedTexts: action.payload.legacyAdvancedTexts || {}
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch playbooks
      .addCase(fetchPlaybooks.pending, (state) => {
        state.playbooksLoading = true;
        state.error = null;
      })
      .addCase(fetchPlaybooks.fulfilled, (state, action) => {
        state.playbooksLoading = false;
        state.playbooks = action.payload || [];
      })
      .addCase(fetchPlaybooks.rejected, (state, action) => {
        state.playbooksLoading = false;
        state.error = action.payload as string;
      })
      // Fetch role data
      .addCase(fetchRoleData.pending, (state) => {
        state.dataLoading = true;
        state.error = null;
      })
      .addCase(fetchRoleData.fulfilled, (state, action) => {
        const requested = action.meta?.arg;
        if (requested && (requested.playbookName !== state.selectedPlaybookName || requested.roleName !== state.selectedRole)) return;
        state.dataLoading = false;
        if (action.payload) {
          state.currentPitch = action.payload.pitch || [];
          state.currentAdvanced = action.payload.advanced || [];
          state.currentPitchItems = resolvePlaybookBullets(state.currentPitch, action.payload.pitchItems, 'pitch', state.legacyPitchTexts);
          state.currentAdvancedItems = resolvePlaybookBullets(state.currentAdvanced, action.payload.advancedItems, 'advanced', state.legacyAdvancedTexts);
          const pitches = migrateLegacyBulletTracking(state.checkedPitches, state.pitchNotes, state.currentPitchItems);
          state.checkedPitches = pitches.checked;
          state.pitchNotes = pitches.notes;
          const advanced = migrateLegacyBulletTracking(state.checkedAdvanced, state.advancedNotes, state.currentAdvancedItems);
          state.checkedAdvanced = advanced.checked;
          state.advancedNotes = advanced.notes;
          const key = state.selectedPlaybookName && getTrackingCacheKey(state.selectedPlaybookName, state.selectedRole);
          if (key && state.trackingCache[key]) {
            state.trackingCache[key].checkedPitches = [...state.checkedPitches];
            state.trackingCache[key].pitchNotes = { ...state.pitchNotes };
            state.trackingCache[key].checkedAdvanced = [...state.checkedAdvanced];
            state.trackingCache[key].advancedNotes = { ...state.advancedNotes };
          }
          state.questions = action.payload.questions || [];
        }
      })
      .addCase(fetchRoleData.rejected, (state, action) => {
        const requested = action.meta?.arg;
        if (requested && (requested.playbookName !== state.selectedPlaybookName || requested.roleName !== state.selectedRole)) return;
        state.dataLoading = false;
        state.error = action.payload as string;
        state.currentPitch = [];
        state.currentAdvanced = [];
        state.currentPitchItems = [];
        state.currentAdvancedItems = [];
        state.questions = [];
      });
  }
});

/**
 * Dropdown-ready playbook options, sorted by playbookName. Entries missing a
 * playbookName (partially-migrated or malformed responses) must not crash the
 * UI: they sort last and render under an 'Unnamed' bucket.
 */
export const selectPlaybookOptions = createSelector(
  (state: { salesPlaybook: SalesPlaybookState }) => state.salesPlaybook.playbooks,
  (playbooks) =>
    [...(playbooks || [])]
      .filter((playbook): playbook is SalesPlaybookInfo => playbook != null)
      .sort((a, b) => {
        if (!a?.playbookName) return b?.playbookName ? 1 : 0;
        if (!b?.playbookName) return -1;
        return a.playbookName.localeCompare(b.playbookName);
      })
      .map(playbook => ({
        ...playbook,
        displayName: playbook?.displayName || playbook?.playbookName || PLAYBOOK_UNNAMED_LABEL
      }))
);

export const {
  setSelectedPlaybook,
  setSelectedRole,
  markQuestionAsked,
  unmarkQuestionAsked,
  toggleQuestionAsked,
  addQuestionNote,
  removeQuestionNote,
  togglePitchChecked,
  addPitchNote,
  removePitchNote,
  toggleAdvancedChecked,
  addAdvancedNote,
  removeAdvancedNote,
  applyLivePlaybookItems,
  clearLiveCheckAnimation,
  clearLiveNoteAnimation,
  resetLivePlaybookItem,
  clearSalesPlaybookSession,
  clearError,
  restoreSalesPlaybookState
} = salesPlaybookSlice.actions;

// Backward compatibility aliases
export const clearTalkTracksSession = clearSalesPlaybookSession;
export const restoreTalkTrackState = restoreSalesPlaybookState;

// Type aliases for backward compatibility
export type TalkTrackTag = SalesPlaybookTag;
export type TalkTrackQuestion = SalesPlaybookQuestion;
export type TalkTrackRoleInfo = SalesPlaybookRoleInfo;

export default salesPlaybookSlice.reducer;
